import { EventLightVm, EventShotLightVm, MatchPlayerVm } from '../../utils/api';
import {
    detectNewGkTrackingByTeam,
    resolveDefendingGkMatchPlayerId,
} from './insightsGoalkeeperZones';
import { playerDisplayName, sortEventsChronological } from './insightsMatchStats';
import { isPlaceholderPlayerName } from './insightsPlayerSeason';
import { MatchBundle } from './insightsQueries';

/*
 * Same TOTALSCORE formula as total-waterpolo-server competition-valorization.ts.
 * Counts come from Arena light events (DF / FB / type are on EventShot).
 * ponytail: light exclusions omit isDoubleExclusion - every assistedBy still counts.
 */

export type ValorShotKind =
    | 'action'
    | 'fastBreak'
    | 'directFromFoul'
    | 'powerplay'
    | 'penalty'
    | 'penaltyShootout';

export interface ValorizationWeights {
    golAction: number;
    golFastBreak: number;
    golDirectFromFoul: number;
    golPowerplay: number;
    golPen: number;
    golPenShootout: number;
    asist: number;
    exclusionAssist: number;
    penaltyAssist: number;
    steal: number;
    block: number;
    pfDrawn: number;
    personalFoul: number;
    turnover: number;
    missedAction: number;
    missedFastBreak: number;
    missedDirectFromFoul: number;
    missedPowerplay: number;
    missedPen: number;
    missedPenShootout: number;
    gkRegularSave: number;
    gkPowerplaySave: number;
    gkPenaltySave: number;
    gkFastBreakSave: number;
    gkPenaltyShootoutSave: number;
    gkGoalConceded: number;
}

export const DEFAULT_VALORIZATION_WEIGHTS: ValorizationWeights = {
    golAction: 1,
    golFastBreak: 1,
    golDirectFromFoul: 1,
    golPowerplay: 1,
    golPen: 1,
    golPenShootout: 0,
    asist: 0.8,
    exclusionAssist: 0.35,
    penaltyAssist: 0.5,
    steal: 0.5,
    block: 0.7,
    pfDrawn: 0.35,
    personalFoul: -0.2,
    turnover: -0.5,
    missedAction: -0.1,
    missedFastBreak: -0.1,
    missedDirectFromFoul: -0.1,
    missedPowerplay: -0.1,
    missedPen: -0.8,
    missedPenShootout: 0,
    gkRegularSave: 1,
    gkPowerplaySave: 1.2,
    gkPenaltySave: 1.3,
    gkFastBreakSave: 1.5,
    gkPenaltyShootoutSave: 0,
    gkGoalConceded: 0,
};

export type ValorWeightGroup = 'goals' | 'plus' | 'minus' | 'misses' | 'gk';

export const VALOR_WEIGHT_FIELDS: {
    key: keyof ValorizationWeights;
    label: string;
    group: ValorWeightGroup;
}[] = [
    { key: 'golAction', label: 'Goal action', group: 'goals' },
    { key: 'golFastBreak', label: 'Goal FB', group: 'goals' },
    { key: 'golDirectFromFoul', label: 'Goal direct foul', group: 'goals' },
    { key: 'golPowerplay', label: 'Goal PP', group: 'goals' },
    { key: 'golPen', label: 'Goal pen', group: 'goals' },
    { key: 'golPenShootout', label: 'Goal pen SO', group: 'goals' },
    { key: 'asist', label: 'Assist', group: 'plus' },
    { key: 'exclusionAssist', label: 'Exclusion assist', group: 'plus' },
    { key: 'penaltyAssist', label: 'Penalty assist', group: 'plus' },
    { key: 'steal', label: 'Steal', group: 'plus' },
    { key: 'block', label: 'Block', group: 'plus' },
    { key: 'pfDrawn', label: 'PF drawn', group: 'plus' },
    { key: 'personalFoul', label: 'Personal foul', group: 'minus' },
    { key: 'turnover', label: 'Turnover', group: 'minus' },
    { key: 'missedAction', label: 'Missed action', group: 'misses' },
    { key: 'missedFastBreak', label: 'Missed FB', group: 'misses' },
    { key: 'missedDirectFromFoul', label: 'Missed direct foul', group: 'misses' },
    { key: 'missedPowerplay', label: 'Missed PP', group: 'misses' },
    { key: 'missedPen', label: 'Missed pen', group: 'misses' },
    { key: 'missedPenShootout', label: 'Missed pen SO', group: 'misses' },
    { key: 'gkRegularSave', label: 'GK regular save', group: 'gk' },
    { key: 'gkPowerplaySave', label: 'GK PP save', group: 'gk' },
    { key: 'gkPenaltySave', label: 'GK pen save', group: 'gk' },
    { key: 'gkFastBreakSave', label: 'GK FB save', group: 'gk' },
    { key: 'gkPenaltyShootoutSave', label: 'GK pen SO save', group: 'gk' },
    { key: 'gkGoalConceded', label: 'GK goal conceded', group: 'gk' },
];

export const VALOR_GROUP_LABELS: Record<ValorWeightGroup, string> = {
    goals: 'Goals by shot type',
    plus: 'Positive',
    minus: 'Negative',
    misses: 'Misses by shot type',
    gk: 'Goalkeeper',
};

const STORAGE_KEY = 'insights.valorization.weights.v1';

export const mergeValorWeights = (
    partial: Partial<ValorizationWeights> | null | undefined
): ValorizationWeights => {
    const w = { ...DEFAULT_VALORIZATION_WEIGHTS };
    if (!partial) return w;
    (Object.keys(w) as (keyof ValorizationWeights)[]).forEach((key) => {
        const v = partial[key];
        if (typeof v === 'number' && Number.isFinite(v)) w[key] = v;
    });
    return w;
};

export const loadValorWeights = (): ValorizationWeights => {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_VALORIZATION_WEIGHTS };
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_VALORIZATION_WEIGHTS };
        return mergeValorWeights(JSON.parse(raw) as Partial<ValorizationWeights>);
    } catch {
        return { ...DEFAULT_VALORIZATION_WEIGHTS };
    }
};

export const saveValorWeights = (weights: ValorizationWeights): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(weights));
};

const emptyKind = (): Record<ValorShotKind, number> => ({
    action: 0,
    fastBreak: 0,
    directFromFoul: 0,
    powerplay: 0,
    penalty: 0,
    penaltyShootout: 0,
});

export interface ValorPlayerCounts {
    playerId: number;
    name: string;
    matches: number;
    goals: Record<ValorShotKind, number>;
    attempts: Record<ValorShotKind, number>;
    assists: number;
    exclusionAssists: number;
    penaltyAssists: number;
    steals: number;
    blocks: number;
    pfDrawn: number;
    personalFouls: number;
    turnovers: number;
    gkSaves: Record<ValorShotKind, number>;
    gkAttempts: Record<ValorShotKind, number>;
}

export interface ValorScoreRow {
    playerId: number;
    name: string;
    matches: number;
    total: number;
    perGame: number;
}

const normalise = (value?: string): string => (value || '').toLowerCase().replace(/[\s_-]/g, '');

/** Same priority as process-events.ts: PS > Pen > PP > FB > DF > action. */
export const valorShotKind = (shot: EventShotLightVm): ValorShotKind => {
    const type = normalise(shot.type);
    if (type === 'penaltyshootout') return 'penaltyShootout';
    if (type === 'penalty') return 'penalty';
    if (type === 'powerplay') return 'powerplay';
    if (shot.isFastBreak) return 'fastBreak';
    if (shot.isDirectFromFoul) return 'directFromFoul';
    return 'action';
};

const isPenaltyExclusion = (type?: string): boolean => {
    const key = normalise(type);
    return key === 'penalty' || key === 'penaltyshootout';
};

const emptyCounts = (playerId: number, name: string): ValorPlayerCounts => ({
    playerId,
    name,
    matches: 0,
    goals: emptyKind(),
    attempts: emptyKind(),
    assists: 0,
    exclusionAssists: 0,
    penaltyAssists: 0,
    steals: 0,
    blocks: 0,
    pfDrawn: 0,
    personalFouls: 0,
    turnovers: 0,
    gkSaves: emptyKind(),
    gkAttempts: emptyKind(),
});

const miss = (goals: number, attempts: number): number => Math.max(0, attempts - goals);

export const scoreValorCounts = (c: ValorPlayerCounts, weights: ValorizationWeights): number => {
    const w = weights;
    let total = 0;
    total += c.goals.action * w.golAction;
    total += c.goals.fastBreak * w.golFastBreak;
    total += c.goals.directFromFoul * w.golDirectFromFoul;
    total += c.goals.powerplay * w.golPowerplay;
    total += c.goals.penalty * w.golPen;
    total += c.goals.penaltyShootout * w.golPenShootout;
    total += miss(c.goals.action, c.attempts.action) * w.missedAction;
    total += miss(c.goals.fastBreak, c.attempts.fastBreak) * w.missedFastBreak;
    total += miss(c.goals.directFromFoul, c.attempts.directFromFoul) * w.missedDirectFromFoul;
    total += miss(c.goals.powerplay, c.attempts.powerplay) * w.missedPowerplay;
    total += miss(c.goals.penalty, c.attempts.penalty) * w.missedPen;
    total += miss(c.goals.penaltyShootout, c.attempts.penaltyShootout) * w.missedPenShootout;
    total += c.assists * w.asist;
    total += c.exclusionAssists * w.exclusionAssist;
    total += c.penaltyAssists * w.penaltyAssist;
    total += c.steals * w.steal;
    total += c.blocks * w.block;
    total += c.pfDrawn * w.pfDrawn;
    total += c.personalFouls * w.personalFoul;
    total += c.turnovers * w.turnover;
    total += (c.gkSaves.action + c.gkSaves.directFromFoul) * w.gkRegularSave;
    total += c.gkSaves.powerplay * w.gkPowerplaySave;
    total += c.gkSaves.penalty * w.gkPenaltySave;
    total += c.gkSaves.fastBreak * w.gkFastBreakSave;
    total += c.gkSaves.penaltyShootout * w.gkPenaltyShootoutSave;
    const concededNonSo = miss(
        c.gkSaves.action +
            c.gkSaves.fastBreak +
            c.gkSaves.directFromFoul +
            c.gkSaves.powerplay +
            c.gkSaves.penalty,
        c.gkAttempts.action +
            c.gkAttempts.fastBreak +
            c.gkAttempts.directFromFoul +
            c.gkAttempts.powerplay +
            c.gkAttempts.penalty
    );
    const concededSo = miss(c.gkSaves.penaltyShootout, c.gkAttempts.penaltyShootout);
    total += (concededNonSo + concededSo) * w.gkGoalConceded;
    return total;
};

export const formatValorScore = (n: number): string => (Math.round(n * 10) / 10).toFixed(1);

export const formatValorPerGame = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);

/**
 * Per-player counts from our club events. GK attribution matches Tw boards.
 */
export const collectValorCounts = (
    bundles: MatchBundle[],
    ourTeamIds: number[]
): ValorPlayerCounts[] => {
    const byId = new Map<number, ValorPlayerCounts>();

    const ensure = (mp: MatchPlayerVm): ValorPlayerCounts | undefined => {
        if (mp.id == null || mp.playerId == null) return undefined;
        if (!ourTeamIds.includes(mp.teamId)) return undefined;
        const name = playerDisplayName(mp);
        if (isPlaceholderPlayerName(name)) return undefined;
        let row = byId.get(mp.playerId);
        if (!row) {
            row = emptyCounts(mp.playerId, name);
            byId.set(mp.playerId, row);
        }
        return row;
    };

    bundles.forEach((bundle) => {
        const lineup = new Map<number, MatchPlayerVm>();
        (bundle.detail.players || []).forEach((mp) => {
            if (mp.id == null) return;
            lineup.set(mp.id, mp);
        });

        lineup.forEach((mp) => {
            const row = ensure(mp);
            if (row) row.matches += 1;
        });

        const newTracking = detectNewGkTrackingByTeam(bundle.events);
        const lastSaverByTeam = new Map<number, number>();
        const pendingAgainstByTeam = new Map<number, ValorShotKind[]>();

        const creditGk = (mpId: number, kind: ValorShotKind, mode: 'save' | 'against'): void => {
            const mp = lineup.get(mpId);
            const row = mp ? ensure(mp) : undefined;
            if (!row) return;
            row.gkAttempts[kind] += 1;
            if (mode === 'save') row.gkSaves[kind] += 1;
        };

        const flushPending = (teamId: number, saverMpId: number): void => {
            const pending = pendingAgainstByTeam.get(teamId);
            if (!pending || pending.length === 0) return;
            if (!lineup.get(saverMpId)) {
                pendingAgainstByTeam.delete(teamId);
                return;
            }
            pending.forEach((kind) => creditGk(saverMpId, kind, 'against'));
            pendingAgainstByTeam.delete(teamId);
        };

        sortEventsChronological(bundle.events).forEach((event: EventLightVm) => {
            if (event.shot) {
                const kind = valorShotKind(event.shot);
                const shooterMp =
                    event.shot.takenById != null ? lineup.get(event.shot.takenById) : undefined;
                const shooter = shooterMp ? ensure(shooterMp) : undefined;
                if (shooter) {
                    shooter.attempts[kind] += 1;
                    if (event.shot.isGoal) shooter.goals[kind] += 1;
                }

                if (
                    event.shot.isGoal &&
                    event.shot.assistedById != null &&
                    kind !== 'penalty' &&
                    kind !== 'penaltyShootout'
                ) {
                    const am = lineup.get(event.shot.assistedById);
                    const assist = am ? ensure(am) : undefined;
                    if (assist) assist.assists += 1;
                }

                if (event.shot.blockedById != null) {
                    const bm = lineup.get(event.shot.blockedById);
                    const blocker = bm ? ensure(bm) : undefined;
                    if (blocker) blocker.blocks += 1;
                }

                if (!ourTeamIds.includes(event.shot.teamId)) {
                    const isGoal = !!event.shot.isGoal;
                    const savedById = event.shot.savedById;

                    if (!isGoal && savedById != null) {
                        const saver = lineup.get(savedById);
                        if (saver && ourTeamIds.includes(saver.teamId)) {
                            flushPending(saver.teamId, savedById);
                            lastSaverByTeam.set(saver.teamId, savedById);
                            creditGk(savedById, kind, 'save');
                        }
                    } else if (isGoal) {
                        const defendingMpId = resolveDefendingGkMatchPlayerId(
                            event,
                            bundle.detail.homeTeamId,
                            bundle.detail.awayTeamId
                        );
                        const defendingRow =
                            defendingMpId != null ? lineup.get(defendingMpId) : undefined;
                        const defendingTeamId =
                            defendingRow?.teamId ??
                            (event.shot.teamId === bundle.detail.homeTeamId
                                ? bundle.detail.awayTeamId
                                : bundle.detail.homeTeamId);
                        const usesNew =
                            event.shootoutOrder != null || (defendingTeamId != null && newTracking.get(defendingTeamId) === true);

                        if (usesNew && defendingMpId != null && defendingRow) {
                            if (ourTeamIds.includes(defendingRow.teamId)) {
                                creditGk(defendingMpId, kind, 'against');
                            }
                        } else {
                            const lastSaverId = lastSaverByTeam.get(defendingTeamId);
                            if (lastSaverId != null) {
                                const lastSaver = lineup.get(lastSaverId);
                                if (lastSaver && ourTeamIds.includes(lastSaver.teamId)) {
                                    creditGk(lastSaverId, kind, 'against');
                                }
                            } else if (defendingTeamId != null) {
                                const pending = pendingAgainstByTeam.get(defendingTeamId) || [];
                                pending.push(kind);
                                pendingAgainstByTeam.set(defendingTeamId, pending);
                            }
                        }
                    }
                }
            }

            if (event.exclusion) {
                if (event.exclusion.excludedPlayerId != null) {
                    const em = lineup.get(event.exclusion.excludedPlayerId);
                    const excluded = em ? ensure(em) : undefined;
                    if (excluded) excluded.personalFouls += 1;
                }
                if (event.exclusion.fouledPlayerId != null) {
                    const fm = lineup.get(event.exclusion.fouledPlayerId);
                    const fouled = fm ? ensure(fm) : undefined;
                    if (fouled) fouled.pfDrawn += 1;
                }
                if (event.exclusion.assistedById != null) {
                    const am = lineup.get(event.exclusion.assistedById);
                    const passer = am ? ensure(am) : undefined;
                    if (passer) {
                        if (isPenaltyExclusion(event.exclusion.type)) passer.penaltyAssists += 1;
                        else passer.exclusionAssists += 1;
                    }
                }
            }

            if (event.turnover) {
                if (event.turnover.lostPossesionPlayerId != null) {
                    const lm = lineup.get(event.turnover.lostPossesionPlayerId);
                    const lost = lm ? ensure(lm) : undefined;
                    if (lost) lost.turnovers += 1;
                }
                if (event.turnover.wonPossesionPlayerId != null) {
                    const wm = lineup.get(event.turnover.wonPossesionPlayerId);
                    const won = wm ? ensure(wm) : undefined;
                    if (won) won.steals += 1;
                }
            }
        });
    });

    return Array.from(byId.values());
};

export const buildValorScoreRows = (
    counts: ValorPlayerCounts[],
    weights: ValorizationWeights,
    visibleIds?: Set<number>
): ValorScoreRow[] =>
    counts
        .filter((c) => !visibleIds || visibleIds.has(c.playerId))
        .map((c) => {
            const total = scoreValorCounts(c, weights);
            return {
                playerId: c.playerId,
                name: c.name,
                matches: c.matches,
                total,
                perGame: c.matches > 0 ? total / c.matches : 0,
            };
        })
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
